import { getAccessToken } from '../lib/supabase';

const API_URL = import.meta.env.VITE_API_URL || '';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: { code: string; message: string };
}

async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const token = await getAccessToken();
  if (!token) {
    return {
      success: false,
      error: { code: 'UNAUTHORIZED', message: 'Not authenticated' },
    };
  }

  try {
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    });

    const json = await response.json() as { success?: boolean; data?: T; error?: { code: string; message: string } };

    if (!response.ok) {
      return {
        success: false,
        error: json.error || {
          code: 'API_ERROR',
          message: `API error: ${response.status}`,
        },
      };
    }

    // Unwrap data from API response if present
    return { success: true, data: json.data };
  } catch (err) {
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: err instanceof Error ? err.message : 'Network error',
      },
    };
  }
}

// Types

/**
 * Instance status values:
 * - stopped: Machine not running (initial state)
 * - creating: First-time Machine creation in progress (80-120s)
 * - starting: Resuming existing Machine (1-10s)
 * - running: Machine is running and ready
 * - suspended: Machine is suspended (scale-to-zero)
 * - deleting: Deletion in progress
 * - deleted: Instance has been deleted (soft delete)
 */
export type InstanceStatus = 'stopped' | 'creating' | 'starting' | 'running' | 'suspended' | 'deleting' | 'deleted';

export interface Instance {
  id: string;
  owner_id: string;
  name: string;
  status: InstanceStatus;
  fly_machine_id: string | null;
  fly_volume_id: string | null;
  last_activity_at: string | null;
  created_at: string;
  deleted_at?: string | null;
}

export interface InstanceDetail {
  instanceId: string;
  name: string;
  ownerId: string;
  createdAt: string;

  // DO state (authoritative/live)
  do: {
    status: string;
    machineId: string | null;
    volumeId: string | null;
    lastActivityAt: number | null;
    lastStartedAt: number | null;
    uptimeMs: number;
  };

  // DB state (cached, may be stale)
  db: {
    status: string;
    machineId: string | null;
    volumeId: string | null;
  };

  // Sync status
  isSynced: boolean;
}

export interface SyncResult {
  status: string;
  machineId: string | null;
  volumeId: string | null;
  message: string;
}

export interface PaginatedInstances {
  instances: Instance[];
  total: number;
  offset: number;
  limit: number;
}

export interface DeleteResult {
  success: boolean;
  deleted: string[];
  errors?: string[];
}

export interface OrphanMachine {
  id: string;
  name: string;
  created_at: string;
  region: string;
  state: string;
}

export interface OrphanVolume {
  id: string;
  name: string;
  created_at: string;
  region: string;
  size_gb: number;
  state?: string;
}

export interface OrphanResources {
  machines: OrphanMachine[];
  volumes: OrphanVolume[];
}

export interface InstanceActionResult {
  instanceId: string;
  status: string;
}

export interface OrphanDeleteResult {
  deleted: boolean;
  type: 'machine' | 'volume';
  id: string;
}

export interface EnvVarsResponse {
  machineId: string;
  env: Record<string, string>;
}

export interface UpdateEnvResult {
  message: string;
}

export interface UpdateImageResult {
  image: string;
}

export interface PurgeResult {
  instanceId: string;
  message: string;
}

export interface MachineDetail {
  id: string;
  name: string;
  state: string;
  region: string;
  created_at: string;
  updated_at: string;
  config: {
    image: string;
    env?: Record<string, string>;
  };
  image_ref?: {
    registry: string;
    repository: string;
    tag: string;
  };
}

// Super Admin API
export const superAdminApi = {
  // List all instances with pagination
  listInstances: (options?: { offset?: number; limit?: number; includeDeleted?: boolean }) => {
    const params = new URLSearchParams();
    if (options?.offset !== undefined) params.set('offset', String(options.offset));
    if (options?.limit !== undefined) params.set('limit', String(options.limit));
    if (options?.includeDeleted) params.set('include_deleted', 'true');
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiRequest<PaginatedInstances>(`/v1/super/instances${query}`);
  },

  // Get single instance detail (real-time from DO)
  getInstance: (instanceId: string) =>
    apiRequest<InstanceDetail>(`/v1/super/instances/${instanceId}`),

  // Sync DO state to database
  syncInstance: (instanceId: string) =>
    apiRequest<SyncResult>(`/v1/super/instances/${instanceId}/sync`, {
      method: 'POST',
    }),

  // Delete an instance
  deleteInstance: (instanceId: string) =>
    apiRequest<DeleteResult>(`/v1/super/instances/${instanceId}`, {
      method: 'DELETE',
    }),

  // Purge DO storage for a deleted instance (completely removes DO state)
  purgeInstance: (instanceId: string) =>
    apiRequest<PurgeResult>(`/v1/super/instances/${instanceId}/purge`, {
      method: 'POST',
    }),

  // Start an instance
  startInstance: (instanceId: string) =>
    apiRequest<InstanceActionResult>(`/v1/super/instances/${instanceId}/start`, {
      method: 'POST',
    }),

  // Stop an instance
  stopInstance: (instanceId: string) =>
    apiRequest<InstanceActionResult>(`/v1/super/instances/${instanceId}/stop`, {
      method: 'POST',
    }),

  // Suspend an instance
  suspendInstance: (instanceId: string) =>
    apiRequest<InstanceActionResult>(`/v1/super/instances/${instanceId}/suspend`, {
      method: 'POST',
    }),

  // Get orphan resources
  getOrphans: () => apiRequest<OrphanResources>('/v1/super/orphans'),

  // Delete single orphan resource
  deleteOrphan: (type: 'machine' | 'volume', id: string) =>
    apiRequest<OrphanDeleteResult>(`/v1/super/orphans/${type}/${id}`, {
      method: 'DELETE',
    }),

  // Cleanup orphan resources (bulk)
  cleanup: (targets: {
    machines?: string[];
    volumes?: string[];
    dryRun?: boolean;
  }) =>
    apiRequest<{
      deleted: { machines: string[]; volumes: string[] };
      failed: {
        machines: Array<{ id: string; error: string }>;
        volumes: Array<{ id: string; error: string }>;
      };
    }>('/v1/super/cleanup', {
      method: 'POST',
      body: JSON.stringify(targets),
    }),

  // Get machine environment variables (read-only, directly from Fly API)
  getMachineEnv: (machineId: string) =>
    apiRequest<EnvVarsResponse>(`/v1/super/fly/machines/${machineId}/environment`),

  // Update instance environment variables (through DO Manager)
  updateInstanceEnv: (instanceId: string, env: Record<string, string>) =>
    apiRequest<UpdateEnvResult>(`/v1/super/instances/${instanceId}/environment`, {
      method: 'POST',
      body: JSON.stringify({ env }),
    }),

  // Get machine details (directly from Fly API, includes image info)
  getMachine: (machineId: string) =>
    apiRequest<MachineDetail>(`/v1/super/fly/machines/${machineId}`),

  // Update instance machine image (through DO Manager)
  // If image is not provided, uses server's FLY_MACHINE_IMAGE config
  updateInstanceImage: (instanceId: string, image?: string) =>
    apiRequest<UpdateImageResult>(`/v1/super/instances/${instanceId}/image`, {
      method: 'POST',
      body: JSON.stringify({ image }),
    }),
};
